/**
 * Los dos componentes invisibles que necesitan VER la navegación.
 *
 * Vivían dentro de `App.tsx`. Se mudaron acá al pasar al router de datos (ADR-0012, paso 2): con
 * `RouterProvider` tienen que colgar de una ruta, y el archivo del router los importa. Dejarlos en
 * `App.tsx` habría creado un ciclo de imports (App → router → App), que en Vite no falla al
 * compilar: falla en runtime con un `undefined` a mitad de la inicialización, que es de los errores
 * más caros de diagnosticar.
 *
 * No renderizan nada. Se montan una vez con la capa raíz y no se vuelven a montar al navegar, así
 * que conservan su estado entre pantallas — igual que antes.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getTenantFromDomain } from '@/lib/tenant-resolver';
import { initDB } from '@/lib/db';
import { startAutoSync, stopAutoSync, setupConnectionListeners } from '@/lib/sync';
import { AUTO_SYNC_INTERVAL } from '@/config/cache.config';
import { getHostname, isAppDomain } from '@/lib/domain';
import { devLog } from '@/lib/utils';

/** Guarda la última ubicación fuera de /settings/* para que la "X" cierre toda Configuración de forma consistente. */
export function LastNonSettingsLocationTracker() {
  const location = useLocation();

  useEffect(() => {
    const fullPath = `${location.pathname}${location.search}${location.hash}`;
    if (!location.pathname.includes('/settings')) {
      window.sessionStorage.setItem('smarty:lastNonSettingsLocation', fullPath);
    }
  }, [location.pathname, location.search, location.hash]);

  return null;
}

/**
 * Inicializa IndexedDB en background y controla Auto-sync según la ruta.
 *
 * `initDB()` corre asíncrono sin bloquear el árbol de rutas: las funciones de
 * `@/lib/db` (cacheProducts, getCachedProductsIfFresh, etc.) hacen `await initDB()`
 * internamente, por lo que los consumidores esperan solo si lo necesitan. Las
 * rutas que no tocan IndexedDB (editor de temas, admin, settings) montan en el
 * primer frame.
 */
export function AutoSyncManager() {
  const location = useLocation();

  useEffect(() => {
    void initDB().catch((error) => {
      devLog.error('Error al inicializar sistema offline:', error);
    });
    setupConnectionListeners();
  }, []);

  useEffect(() => {
    const hasSubdomain = !!getTenantFromDomain();
    const hostname = getHostname();
    const appDomain = isAppDomain(hostname);
    const isEcommerceRoute =
      !appDomain &&
      (hasSubdomain ||
        location.pathname.startsWith('/public') ||
        location.pathname.startsWith('/tienda') ||
        location.pathname.startsWith('/menu'));

    if (isEcommerceRoute) {
      stopAutoSync();
    } else {
      startAutoSync(AUTO_SYNC_INTERVAL);
    }
    return () => {
      if (isEcommerceRoute) stopAutoSync();
    };
  }, [location.pathname]);

  return null;
}
